import React, { useState, useEffect, useRef } from 'react';
import {
  XMarkIcon,
  CalendarIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentCheckIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  MapPinIcon,
  PhoneIcon,
  ClockIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { auth } from '../../../firebase';
import axios from 'axios';

const AppointmentModal = ({ isOpen, onClose, selectedCar, onAppointmentComplete }) => {
  // Store original body styles and scroll position
  const bodyStyles = useRef({});

  // State for multi-step process
  const [currentStep, setCurrentStep] = useState(1); // 1: service, 2: city, 3: date, 4: shop, 5: confirmation, 6: success
  const [selectedService, setSelectedService] = useState(null);
  const [selectedServiceName, setSelectedServiceName] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // States for API data

  // Create a robust scroll lock mechanism
  useEffect(() => {
    if (isOpen) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Store current scroll position and styles
      bodyStyles.current = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        mainOverflow: mainElement?.style.overflow,
      };

      // Apply scroll lock styles
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Also lock the main scrollable element
      if (mainElement) {
        mainElement.style.overflow = 'hidden';
      }
    } else if (bodyStyles.current.scrollY !== undefined) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Restore original styles
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element overflow
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      // Restore scroll position
      if (mainElement) {
        mainElement.scrollTop = bodyStyles.current.scrollY;
      } else {
        window.scrollTo(0, bodyStyles.current.scrollY);
      }
    }

    return () => {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Always restore on unmount
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      if (bodyStyles.current.scrollY !== undefined) {
        if (mainElement) {
          mainElement.scrollTop = bodyStyles.current.scrollY;
        } else {
          window.scrollTo(0, bodyStyles.current.scrollY);
        }
      }
    };
  }, [isOpen]);
  const [cities, setCities] = useState([]);
  const [shops, setShops] = useState([]);
  const [expandedShopId, setExpandedShopId] = useState(null);
  const [selectedShop, setSelectedShop] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Appointment confirmation states
  const [appointmentData, setAppointmentData] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingError, setBookingError] = useState(null);

  // Reset the modal state when it opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setSelectedService(null);
      setSelectedServiceName(null);
      setSelectedCity(null);
      setSelectedDate(null);
      setShops([]);
      setExpandedShopId(null);
      setSelectedShop(null);
      setSelectedSlot(null);
      setError(null);
      setAppointmentData(null);
      setIsBooking(false);
      setBookingError(null);
    }
  }, [isOpen]);

  // Fetch cities when needed
  useEffect(() => {
    if (isOpen && currentStep === 2 && cities.length === 0) {
      fetchCities();
    }
  }, [isOpen, currentStep, cities.length]);

  // Fetch shops when needed
  useEffect(() => {
    if (isOpen && currentStep === 4 && selectedService && selectedCity && selectedDate) {
      fetchAvailableShops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentStep, selectedService, selectedCity, selectedDate]);

  // Service types data
  const serviceTypes = [
    { id: 'ITP', name: 'ITP', icon: ClipboardDocumentCheckIcon },
    { id: 'MECHANICAL', name: 'Reparație Mecanică', icon: WrenchScrewdriverIcon },
    // { id: 'ELECTRICAL', name: 'Reparație Electrică', icon: BoltIcon }
  ];

  // Fetch cities from API
  const fetchCities = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('https://api.servicegest.ro/online/cities');
      if (!response.ok) throw new Error('Failed to fetch cities');
      const data = await response.json();
      setCities(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching cities:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch available shops from API
  const fetchAvailableShops = async () => {
    setIsLoading(true);
    setError(null);
    setSelectedShop(null);
    setSelectedSlot(null);
    
    try {
      // Format the query parameters
      const queryParams = new URLSearchParams({
        specialization: selectedService,
        date: selectedDate.format('YYYY-MM-DD'),
        zone: 'Europe/Bucharest',
        city: selectedCity
      });

      const response = await fetch(`https://api.servicegest.ro/online/slots?${queryParams}`);
      if (!response.ok) throw new Error('Failed to fetch available shops');
      
      const data = await response.json();
      setShops(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching shops:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle service selection
  const handleServiceSelect = (serviceId, serviceName) => {
    setSelectedService(serviceId);
    setSelectedServiceName(serviceName);
    setCurrentStep(2); // Move to city selection
  };

  // Handle city selection
  const handleCitySelect = (city) => {
    setSelectedCity(city);
    // Set today's date as default when moving to date selection
    setSelectedDate(dayjs());
    setCurrentStep(3); // Move to date selection
  };

  // Handle date selection
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  // Handle date confirmation
  const handleDateConfirm = () => {
    setCurrentStep(4); // Move to shop selection
  };

  // Toggle expanded shop
  const toggleExpandShop = (shopId) => {
    if (expandedShopId === shopId) {
      setExpandedShopId(null);
    } else {
      setExpandedShopId(shopId);
    }
  };

  // Format time slot
  const formatTimeSlot = (start, end) => {
    const startTime = new Date(start).toLocaleTimeString('ro-RO', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
    
    const endTime = new Date(end).toLocaleTimeString('ro-RO', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
    
    return `${startTime} - ${endTime}`;
  };

  // Handle slot selection and move to confirmation step
  const handleSelectSlot = (shop, slot) => {
    setSelectedShop(shop);
    setSelectedSlot(slot);
    setBookingError(null);
    setCurrentStep(5); // Move to confirmation step
  };

  // Create appointment API call
  const createAppointment = async () => {
    setIsBooking(true);
    setBookingError(null);
    
    try {
      // Format the appointment time with Bucharest timezone offset
      const appointmentDate = new Date(selectedSlot.slotStart);
      
      // Check if DST is active (roughly April-October)
      const month = appointmentDate.getMonth();
      const isDST = month >= 3 && month <= 9; // April (3) to October (9)
      const offsetString = isDST ? "+03:00" : "+02:00";
      
      // Format as ISO string and append timezone offset
      let appointmentStartFormatted = selectedSlot.slotStart;
      // Remove any existing timezone info (Z, +00:00, etc.)
      appointmentStartFormatted = appointmentStartFormatted.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
      // Append the Bucharest timezone offset
      appointmentStartFormatted = `${appointmentStartFormatted}${offsetString}`;
      
      const appointmentPayload = {
        shopId: selectedShop.shopId,
        registrationNumber: selectedCar.plate,
        appointmentStart: appointmentStartFormatted,
        specialization: selectedService
      };
      
      console.log('Creating appointment with payload:', appointmentPayload);
      
      const idToken = await auth.currentUser?.getIdToken();
      const response = await axios.post(
        'https://api.servicegest.ro/driver/car/appointment',
        appointmentPayload,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      // Success - appointment is confirmed
      setAppointmentData(response.data);
      
      // Move to success step instead of closing
      setCurrentStep(6);
      
      // Complete the appointment process
      onAppointmentComplete({
        shop: selectedShop,
        slot: selectedSlot,
        service: selectedServiceName,
        serviceType: selectedService,
        city: selectedCity,
        date: selectedDate.format('YYYY-MM-DD'),
        car: selectedCar,
        appointmentId: response.data.id
      });
      
    } catch (err) {
      setBookingError(err.response?.data?.message || err.message || 'Failed to create appointment');
      console.error('Error creating appointment:', err);
    } finally {
      setIsBooking(false);
    }
  };

  // Handle back button navigation
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Clear errors when going back
      setError(null);
      setBookingError(null);
      
      // If going back from confirmation (step 5), don't clear shop/slot selection
      // If going back from shop selection (step 4), don't clear date
      // This preserves all user selections
    }
  };

  // Handle changing filters (go back to step 1)
  const handleChangeFilters = () => {
    setCurrentStep(1);
    setSelectedService(null);
    setSelectedServiceName(null);
    setSelectedCity(null);
    setSelectedDate(null);
    setShops([]);
    setExpandedShopId(null);
    setSelectedShop(null);
    setSelectedSlot(null);
    setError(null);
  };

  // Render selection summary
  const renderSelectionSummary = () => {
    if (!selectedService && !selectedCity && !selectedDate) return null;

    return (
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl shadow-sm border border-purple-200 dark:border-purple-800 mb-6 overflow-hidden">
        <div className="p-2 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4">
            <h3 className="text-sm sm:text-base font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400" />
              Criteriile selectate
            </h3>
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            {selectedService && (
              <button
                type="button"
                onClick={() => { setCurrentStep(1); }}
                title="Editează serviciul"
                className="flex items-center gap-2 bg-white dark:bg-slate-800 text-purple-700 dark:text-purple-300 px-3 py-2 rounded-lg border-2 border-purple-300 dark:border-purple-700 shadow-sm max-w-full hover:shadow-md hover:ring-2 hover:ring-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-300 transition-all"
              >
                <WrenchScrewdriverIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-semibold truncate">{selectedServiceName}</span>
              </button>
            )}

            {selectedCity && (
              <button
                type="button"
                onClick={() => { setCurrentStep(2); }}
                title="Schimbă orașul"
                className="flex items-center gap-2 bg-white dark:bg-slate-800 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-lg border-2 border-blue-300 dark:border-blue-700 shadow-sm max-w-full hover:shadow-md hover:ring-2 hover:ring-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-300 transition-all"
              >
                <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-semibold truncate">{selectedCity}</span>
              </button>
            )}

            {selectedDate && (
              <button
                type="button"
                onClick={() => { setCurrentStep(3); }}
                title="Editează data"
                className="flex items-center gap-2 bg-white dark:bg-slate-800 text-green-700 dark:text-green-300 px-3 py-2 rounded-lg border-2 border-green-300 dark:border-emerald-700 shadow-sm max-w-full hover:shadow-md hover:ring-2 hover:ring-green-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-300 transition-all"
              >
                <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-semibold truncate">{selectedDate.format("DD MMMM YYYY")}</span>
              </button>
            )}

            {selectedShop && (
              <button
                type="button"
                onClick={() => { setCurrentStep(4); }}
                title="Vezi service-urile"
                className="flex items-center gap-2 bg-white dark:bg-slate-800 text-orange-700 dark:text-orange-300 px-3 py-2 rounded-lg border-2 border-orange-300 dark:border-orange-700 shadow-sm max-w-full hover:shadow-md hover:ring-2 hover:ring-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-orange-300 transition-all"
              >
                <BuildingOfficeIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-semibold truncate">{selectedShop.brandName}</span>
              </button>
            )}

            {selectedSlot && (
              <button
                type="button"
                onClick={() => { setCurrentStep(4); }}
                title="Editează ora"
                className="flex items-center gap-2 bg-white dark:bg-slate-800 text-pink-700 dark:text-pink-300 px-3 py-2 rounded-lg border-2 border-pink-300 dark:border-pink-700 shadow-sm max-w-full hover:shadow-md hover:ring-2 hover:ring-pink-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-pink-300 transition-all"
              >
                <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-semibold truncate">{new Date(selectedSlot.slotStart).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Return null if modal not open
  if (!isOpen || !selectedCar) return null;

  const darkTheme = createTheme({
    palette: {
      mode: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
      primary: {
        main: '#3b82f6', // purple-500
      },
      background: {
        paper: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff', // gray-800 : white
      },
      text: {
        primary: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827', // gray-100 : gray-900
      }
    },
  });

  // Render steps based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              {serviceTypes.map((service) => (
                <button
                  key={service.id}
                  onClick={() => handleServiceSelect(service.id, service.name)}
                  className="flex items-center p-4 border rounded-lg hover:bg-slate-50 transition-colors dark:border-slate-700 dark:hover:bg-slate-700"
                >
                  <div className="flex items-center flex-1">
                    <div className="p-2 bg-purple-100 rounded-full mr-3 dark:bg-purple-900">
                      <service.icon className="h-6 w-6 text-purple-600 dark:text-purple-300" />
                    </div>
                    <span className="font-medium text-slate-800 dark:text-slate-200">{service.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            {renderSelectionSummary()}
            <p className="text-slate-600 dark:text-slate-300">
              Programare {selectedServiceName} pentru {selectedCar.make} {selectedCar.model} ({selectedCar.plate})
            </p>
            
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <ArrowPathIcon className="h-8 w-8 text-purple-500 animate-spin" />
                <p className="mt-2 text-slate-600 dark:text-slate-300">Se încarcă orașele...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-8">
                <p className="text-rose-500 mb-4">Nu am putut încărca orașele. Vă rugăm să încercați din nou.</p>
                <button 
                  onClick={fetchCities}
                  className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
                >
                  Reîncercați
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 mt-4 max-h-80 overflow-y-auto">
                {cities.map((city) => (
                  <button
                    key={city}
                    onClick={() => handleCitySelect(city)}
                    className="flex items-center p-4 border rounded-lg hover:bg-slate-50 transition-colors dark:border-slate-700 dark:hover:bg-slate-700"
                  >
                    <div className="flex items-center flex-1">
                      <div className="p-2 bg-purple-100 rounded-full mr-3 dark:bg-purple-900">
                        <BuildingOfficeIcon className="h-5 w-5 text-purple-600 dark:text-purple-300" />
                      </div>
                      <span className="font-medium text-slate-800 dark:text-slate-200">
                        {city}
                      </span>
                    </div>
                  </button>
                ))}
                
                {cities.length === 0 && !isLoading && !error && (
                  <p className="text-center py-4 text-slate-500">Nu există orașe disponibile.</p>
                )}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            {renderSelectionSummary()}
            <p className="text-slate-600 dark:text-slate-300">
              Programare {selectedServiceName} în {selectedCity} pentru {selectedCar.make} {selectedCar.model} ({selectedCar.plate})
            </p>
            
            <div className="flex justify-center my-4">
              <ThemeProvider theme={darkTheme}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Selectați data"
                    value={selectedDate}
                    onChange={handleDateChange}
                    disablePast
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        variant: "outlined" 
                      } 
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(156, 163, 175, 0.5)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(59, 130, 246, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3b82f6',
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </div>

            {selectedDate && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleDateConfirm}
                  className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
                >
                  Caută service-uri
                </button>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            {renderSelectionSummary()}
            <p className="text-slate-600 dark:text-slate-300">
              Programare {selectedServiceName} în {selectedCity} pe data de {selectedDate ? selectedDate.format('DD.MM.YYYY') : ''}
            </p>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <ArrowPathIcon className="h-8 w-8 text-purple-500 animate-spin" />
                <p className="mt-2 text-slate-600 dark:text-slate-300">Se încarcă service-urile disponibile...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-8">
                <p className="text-rose-500 mb-4">Nu am putut încărca service-urile disponibile. Vă rugăm să încercați din nou.</p>
                <button 
                  onClick={fetchAvailableShops}
                  className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
                >
                  Reîncercați
                </button>
              </div>
            ) : shops.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <p className="text-slate-600 dark:text-slate-300 mb-4">Nu există service-uri disponibile pentru această dată.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {shops.map((shop) => (
                  <div 
                    key={shop.shopId} 
                    className="border rounded-lg overflow-hidden dark:border-slate-700"
                  >
                    <div 
                      className="p-4 bg-slate-50 dark:bg-slate-700 cursor-pointer"
                      onClick={() => toggleExpandShop(shop.shopId)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-lg mb-1 dark:text-white">
                            {shop.brandName} - <span className="bg-purple-100 text-purple-800 rounded-md px-4 py-1 text-xs font-medium dark:bg-purple-700 dark:text-white">
                              {shop.availableSlots.length} locuri
                            </span>
                          </h4>
                          <div className="flex items-center text-sm text-slate-600 dark:text-slate-300 mb-1">
                            <MapPinIcon className="h-4 w-4 mr-1" />
                            <span>{shop.address}</span>
                          </div>
                          <div className="flex items-center text-sm text-slate-600 dark:text-slate-300">
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            <span>{shop.phone}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {expandedShopId === shop.shopId && (
                      <div className="p-4 border-t dark:border-slate-700">
                        <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                          Selectați un interval orar disponibil:
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {shop.availableSlots.map((slot, index) => (
                            <button
                              key={index}
                              onClick={() => handleSelectSlot(shop, slot)}
                              className="flex dark:text-white items-center justify-center p-2 border rounded-md hover:bg-purple-50 dark:hover:bg-purple-900 dark:border-slate-600"
                            >
                              <ClockIcon className="h-4 w-4 mr-1 text-purple-700" />
                              <span className="text-sm">{formatTimeSlot(slot.slotStart, slot.slotEnd)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 5:
        // Confirmation step
        return (
          <div className="space-y-4">
            {renderSelectionSummary()}
            
            <div className="bg-slate-50 dark:bg-slate-700 p-2 rounded-lg">
              <h4 className="font-medium text-slate-900 dark:text-white mb-4 text-lg">Detalii Programare:</h4>
              <div className="space-y-2 text-slate-600 dark:text-slate-300">
                <p><span className="font-semibold">Serviciu:</span> {selectedServiceName}</p>
                <p><span className="font-semibold">Service:</span> {selectedShop?.brandName}</p>
                <p><span className="font-semibold">Adresă:</span> {selectedShop?.address}</p>
                <p><span className="font-semibold">Telefon:</span> {selectedShop?.phone}</p>
                <p><span className="font-semibold">Data:</span> {selectedDate ? selectedDate.format('DD MMMM YYYY') : ''}</p>
                <p><span className="font-semibold">Ora:</span> {formatTimeSlot(selectedSlot?.slotStart, selectedSlot?.slotEnd)}</p>
                <p><span className="font-semibold">Mașină:</span> {selectedCar.make} {selectedCar.model} ({selectedCar.plate})</p>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                Confirmați că doriți să programați această vizită?
              </p>
              
              {bookingError && (
                <div className="p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-rose-600 dark:text-rose-400">{bookingError}</p>
                </div>
              )}

              <div className="flex justify-center">
                <button
                  onClick={createAppointment}
                  disabled={isBooking}
                  className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center font-medium"
                >
                  {isBooking ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                      Se creează...
                    </>
                  ) : (
                    'Confirmă Programarea'
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      case 6:
        // Success step
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-6">
                  <CheckCircleIcon className="h-16 w-16 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                  🎉 Programare confirmată cu succes!
                </h3>
                <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                  Programarea ta a fost înregistrată
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 p-6 rounded-xl border-2 border-emerald-200 dark:border-emerald-800">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4 text-lg flex items-center gap-2">
                <CalendarIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                Detalii programare
              </h4>
              <div className="space-y-3 text-slate-700 dark:text-slate-300">
                <div className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <WrenchScrewdriverIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Serviciu</p>
                    <p className="font-semibold">{selectedServiceName}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <BuildingOfficeIcon className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 dark:text-slate-400">Service</p>
                    <p className="font-semibold">{selectedShop?.brandName}</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{selectedShop?.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <PhoneIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Telefon</p>
                    <p className="font-semibold">{selectedShop?.phone}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                    <CalendarIcon className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Data</p>
                      <p className="font-semibold">{selectedDate ? selectedDate.format('DD.MM.YYYY') : ''}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                    <ClockIcon className="h-5 w-5 text-pink-600 dark:text-pink-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Ora</p>
                      <p className="font-semibold">{formatTimeSlot(selectedSlot?.slotStart, selectedSlot?.slotEnd)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <span className="font-semibold">📧 Vei primi o notificare</span> cu detaliile programării și un reminder înainte de dată.
              </p>
              {appointmentData?.id && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  ID programare: <span className="font-mono font-semibold">{appointmentData.id}</span>
                </p>
              )}
            </div>

            <div className="flex justify-center pt-4">
              <button
                onClick={onClose}
                className="px-8 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
              >
                Închide
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Get step title for display
  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Selectați Serviciul';
      case 2: return 'Selectați Orașul';
      case 3: return 'Selectați Data';
      case 4: return 'Selectați Service-ul & Ora';
      case 5: return 'Confirmare Programare';
      case 6: return 'Programare Confirmată';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 z-20 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
      <div className="relative w-full max-w-5xl mb-8 max-h-[calc(90vh-4rem)] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700">
        {/* Header */}
        <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 rounded-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <CalendarIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white">
                  Programare {selectedCar.plate}
                </h2>
                {/* <p className="text-sm text-slate-600 dark:text-slate-400">{getStepTitle()}</p> */}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Step Indicator */}
          {/* {currentStep < 6 && (
            <div className="flex items-center justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((step) => (
                <React.Fragment key={step}>
                  <div 
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all ${
                      step === currentStep
                        ? 'bg-purple-600 text-white scale-110'
                        : step < currentStep
                        ? 'bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300'
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    {step}
                  </div>
                  {step < 5 && (
                    <div 
                      className={`h-1 w-8 rounded transition-all ${
                        step < currentStep
                          ? 'bg-purple-200 dark:bg-purple-800'
                          : 'bg-slate-200 dark:bg-slate-700'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )} */}
        </div>
        
        {/* Content */}
        <div className="p-2 overflow-y-auto flex-1">
          {renderStepContent()}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center rounded-b-xl flex-shrink-0">
          <div>
            {currentStep > 1 && currentStep < 6 && (
              <button
                type="button"
                className="bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                onClick={handleBack}
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Înapoi
              </button>
            )}
          </div>
          {currentStep < 6 && (
            <button
              type="button"
              className="bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium py-2 px-4 rounded-lg transition-colors"
              onClick={onClose}
            >
              Închide
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentModal;
